import type { ReportRemoteExportResult, SheetShareFailure } from './export.types'
import { replaceInvalidReportNumbers } from './reportResponse.wire'

import { ApiError } from '@/shared/http'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function exportCodecError(error: unknown): ApiError {
  const record = isRecord(error) ? error : null
  const code = typeof record?.code === 'string' ? record.code : null
  const detail = typeof record?.message === 'string' ? record.message : 'Malformed export response'
  return new ApiError({
    kind: 'unknown',
    status: null,
    code,
    fields: {},
    payload: error,
    userVisible: true,
    detail,
  })
}

function parsedExportBody(body: unknown): Record<string, unknown> {
  const parsed: unknown =
    typeof body === 'string' ? JSON.parse(replaceInvalidReportNumbers(body)) : body
  if (!isRecord(parsed)) throw exportCodecError(parsed)
  return parsed
}

function exportRecipients(body: Record<string, unknown>): string[] {
  if (Array.isArray(body.recipients)) {
    return body.recipients.filter(
      (item): item is string => typeof item === 'string' && Boolean(item),
    )
  }
  if (typeof body.recipient !== 'string') return []
  return body.recipient
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && Boolean(item))
    : []
}

function shareFailures(value: unknown): SheetShareFailure[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((item) => {
    if (!isRecord(item) || typeof item.email !== 'string' || !item.email) return []
    return [
      {
        email: item.email,
        reason: typeof item.reason === 'string' ? item.reason : '',
        retryable: item.retryable === true,
      },
    ]
  })
}

function asyncExportResult(body: Record<string, unknown>): ReportRemoteExportResult | null {
  if (
    (body.export_format !== 'async_csv' && body.export_format !== 'async_xlsx') ||
    body.export_status !== 'processing'
  ) {
    return null
  }
  const recipients = exportRecipients(body)
  if (recipients.length === 0) throw exportCodecError(body)
  return {
    kind: 'async',
    format: body.export_format,
    status: 'processing',
    recipients,
  }
}

/** Strict union decoder for controller `sheets` and async CSV/XLSX acknowledgements. */
export function parseReportExportResponseBody(body: unknown): ReportRemoteExportResult {
  const parsed = parsedExportBody(body)
  const asyncResult = asyncExportResult(parsed)
  if (asyncResult) return asyncResult

  if (typeof parsed.export_url === 'string' && parsed.export_url.trim()) {
    return {
      kind: 'sheet',
      url: parsed.export_url,
      warning: typeof parsed.export_warning === 'string' ? parsed.export_warning : null,
      shared: typeof parsed.shared === 'boolean' ? parsed.shared : null,
      shareReason: typeof parsed.share_reason === 'string' ? parsed.share_reason : null,
      sharedWith: stringList(parsed.shared_with),
      shareFailures: shareFailures(parsed.share_failures),
    }
  }
  if (parsed.export_error !== undefined) throw exportCodecError(parsed.export_error)
  throw exportCodecError(parsed)
}
